import React, { useState, useEffect } from 'react';
import { Select, Tag, Alert, Modal } from 'antd';
import CBModal from '../../../components/CBModal';
import axiosInstance from '../../../variables/api/axiosInstance';
import { STATUTS_SINISTRE, STATUT_MAP, HAB, can } from '../../../variables/constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const GREEN = '#1a6b3c';

/**
 * Modal de changement de statut - partagé entre ListSinistres et DetailsSinistre.
 *
 * Le backend expose `statutsSuivantsAutorises` sur le DTO sinistre :
 *  - array non vide → seuls ces statuts sont proposés (transitions légales,
 *    ou tous les statuts pour un dossier issu de la reprise historique) ;
 *  - champ absent/vide (backend pas encore à jour) → liste complète, comme avant.
 *
 * Props :
 *  - open, onClose : contrôle d'ouverture
 *  - sinistre : DTO sinistre (liste ou détail)
 *  - sinistreId : identifiant à utiliser dans l'URL du PATCH ; par défaut
 *    `sinistre.sinistreTrackingId` (DetailsSinistre passe l'id des params d'URL)
 *  - onChanged : callback après succès, pour rafraîchir la page appelante
 */
const ChangerStatutModal = ({ open, sinistre, sinistreId, onClose, onChanged }) => {
  const { t } = useTranslation('dossierSinistre');
  const [statut, setStatut] = useState('');
  const [loading, setLoading] = useState(false);

  // Repart d'une sélection vierge à chaque ouverture / changement de dossier.
  useEffect(() => {
    if (open) setStatut('');
  }, [open, sinistre]);

  const autorises = Array.isArray(sinistre?.statutsSuivantsAutorises) && sinistre.statutsSuivantsAutorises.length > 0
    ? sinistre.statutsSuivantsAutorises
    : null;

  const options = (autorises
    ? autorises.map((code) => STATUT_MAP[code] || { value: code, label: code, color: 'default' })
    : STATUTS_SINISTRE
  ).filter(s =>
    // Clôture et réouverture : habilitations dédiées (le backend refuse de toute façon).
    (s.value !== 'CLOTURE' || can(HAB.SINISTRE_CLOTURER)) &&
    (s.value !== 'REOUVERT' || can(HAB.SINISTRE_REOUVRIR)));

  const confirmer = async () => {
    if (!statut) return toast.error(t('changerStatut.selectionnez'));
    const idCible = sinistreId || sinistre?.sinistreTrackingId;
    setLoading(true);
    try {
      await axiosInstance.patch(`/v1/sinistres/${idCible}/statut`, null, { params: { statut } });
      toast.success(t('changerStatut.misAJour'));
      onChanged?.();
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        Modal.confirm({
          title: t('changerStatut.cloturerTitre'),
          content: t('changerStatut.cloturerQuandMeme', { message: err?.response?.data?.message || t('changerStatut.compartimentsNonSoldes') }),
          okText: t('changerStatut.cloturer'),
          cancelText: t('changerStatut.annuler'),
          onOk: async () => {
            await axiosInstance.patch(`/v1/sinistres/${idCible}/statut`, null, { params: { statut, confirmer: true } });
            toast.success(t('changerStatut.misAJour'));
            onChanged?.();
            onClose?.();
          },
        });
      } else {
        toast.error(err?.response?.data?.message || t('changerStatut.erreur'));
      }
    } finally { setLoading(false); }
  };

  return (
    <CBModal title={t('changerStatut.titre')} open={open} onCancel={onClose}
      onOk={confirmer} okText={t('changerStatut.confirmer')} confirmLoading={loading}
      okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}>
      <p style={{ marginBottom: 12, color: '#6b7280' }}>
        {t('changerStatut.dossier')} <strong>{sinistre?.numeroSinistreLocal}</strong>
      </p>
      {sinistre?.repriseHistorique && (
        <Alert
          type="info"
          showIcon
          message={t('changerStatut.dossierRepris')}
          style={{ marginBottom: 12 }}
        />
      )}
      <Select
        value={statut || undefined}
        onChange={setStatut}
        style={{ width: '100%' }}
        placeholder={t('changerStatut.nouveauStatut')}
      >
        {options.map(s => (
          <Option key={s.value} value={s.value}>
            <Tag color={s.color} style={{ borderRadius: 20 }}>{s.label}</Tag>
          </Option>
        ))}
      </Select>
    </CBModal>
  );
};

export default ChangerStatutModal;
